import { useEffect, useRef, useState } from "react";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Info, Check } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";

interface VenueAddressFieldProps {
  form: any;
  fieldPrefix?: string;
}

interface AddressSuggestion {
  place_name: string;
  center: [number, number];
  place_type?: string[];
  context?: Array<{ id: string; text: string }>;
}

const VenueAddressField = ({ form, fieldPrefix = "" }: VenueAddressFieldProps) => {
  const venueAddressField = fieldPrefix ? `${fieldPrefix}.venueAddress` : "venueAddress";
  const venueAddressVerifiedField = fieldPrefix ? `${fieldPrefix}.venueAddressVerified` : "venueAddressVerified";
  const venueCoordinatesField = fieldPrefix ? `${fieldPrefix}.venueCoordinates` : "venueCoordinates";
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  
  // Watch for saved venue data
  const savedAddress = form.watch(venueAddressField as any);
  const savedVerified = form.watch(venueAddressVerifiedField as any);
  const savedCoords = form.watch(venueCoordinatesField as any);

  // Don't reset venue address when switching modes - let it persist
  const hasConfirmedDates = form.watch("hasConfirmedDates");

  // Load saved venue data
  useEffect(() => {
    if (!savedAddress || !savedVerified || !savedCoords) return;
    
    // Set the input and verified state
    if (inputValue !== savedAddress) {
      setInputValue(savedAddress);
      setIsVerified(true);
    }
  }, [savedAddress, savedVerified, savedCoords, inputValue]);


  const handleAddressInput = (value: string) => {
    setInputValue(value);
    setIsVerified(false);
    form.setValue(venueAddressVerifiedField as any, false);
    
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (value.length > 2) {
      debounceTimer.current = setTimeout(() => {
        const mapboxToken = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN;
        if (!mapboxToken) {
          console.error("Mapbox token not found");
          return;
        }
        
        fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(value)}.json?access_token=${mapboxToken}&types=place,locality,neighborhood,address,poi&limit=8&autocomplete=true`
        )
          .then((res) => res.json())
          .then((data) => {
            if (data.features) {
              setSuggestions(data.features);
              setShowSuggestions(true);
            }
          })
          .catch((error) => {
            console.error("Error fetching suggestions:", error);
          });
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: AddressSuggestion) => {
    const [lng, lat] = suggestion.center;
    const address = suggestion.place_name;
    
    form.setValue(venueAddressField as any, address, { shouldValidate: true });
    form.setValue(venueAddressVerifiedField as any, true);
    form.setValue(venueCoordinatesField as any, { lng, lat });
    setInputValue(address);
    setIsVerified(true);
    setShowSuggestions(false);
    setSuggestions([]);
    
    // Mark the event as unsaved if this is part of an event
    if (fieldPrefix && fieldPrefix.includes("eventDates")) {
      const match = fieldPrefix.match(/eventDates\.(\d+)/);
      if (match) {
        const eventIndex = parseInt(match[1]);
        const currentDates = form.getValues("eventDates") || [];
        if (currentDates[eventIndex]) {
          currentDates[eventIndex].saved = false;
          form.setValue("eventDates", currentDates);
        }
      }
    }
  };

  return (
    <FormField
      control={form.control}
      name={venueAddressField as any}
      render={({ field, fieldState }) => (
        <FormItem>
          <div className="flex items-center gap-2 mb-1">
            <FormLabel className={(inputValue && !isVerified) ? "text-destructive" : ""}>Venue Address</FormLabel>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">This is flexible and can be updated later. We just need a general location to help plan your event.</p>
              </TooltipContent>
            </Tooltip>
            {isVerified && savedCoords && (
              <Badge 
                variant="secondary" 
                className="relative gap-1 py-0.5 px-2 text-[10px] font-bold tracking-tight bg-gradient-to-r from-green-500/25 via-green-400/25 to-green-500/25 text-green-600 dark:text-green-400 border border-green-500/60 shadow-md shadow-green-500/25 rounded overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/25 before:to-transparent before:animate-[shimmer_2s_ease-in-out_infinite] animate-fade-in"
              >
                <Check className="h-2.5 w-2.5 relative z-10 drop-shadow-[0_0_6px_rgba(34,197,94,1)] stroke-[3]" />
                <span className="relative z-10">Verified</span>
              </Badge>
            )}
          </div>
          <div className="relative">
            <FormControl>
              <Input
                placeholder="Search venue or address..."
                value={inputValue}
                onChange={(e) => {
                  handleAddressInput(e.target.value);
                  field.onChange(e.target.value);
                }}
                className={`bg-background/50 ${(fieldState.error && !isVerified) ? "border-destructive" : ""}`}
              />
            </FormControl>
            
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-auto">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    className="w-full px-3 py-2 text-left hover:bg-accent hover:text-accent-foreground transition-colors text-sm"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion.place_name}
                  </button>
              ))}
            </div>
          )}
          </div>

          {form.formState.errors.venueAddress && form.formState.isSubmitted && !isVerified && (
            <p className="text-sm font-medium text-destructive mt-1">
              {form.formState.errors.venueAddress.message as string}
            </p>
          )}
        </FormItem>
      )}
    />
  );
};

export default VenueAddressField;
